import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ptBR } from 'date-fns/locale/pt-BR';
import Select from "react-select";
import { toast } from "react-toastify";
import { apiService } from '../../../../services/api';
import { Empenho, EtapaEmpenho, CondicaoPagamento, FornecedorDadoBancario, SolicitacaoCompra } from '../../../../types/empenhos';
import AnexoUpload from './AnexoUpload';
import SelecionarDadosBancarios from './SelecionarDadosBancarios';
import QuickFornecedorForm from '../../deals/components/QuickFornecedorForm';

registerLocale('pt-BR', ptBR as any);

interface FormAvancarEtapaProps {
  empenho: Empenho;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FormAvancarEtapa: React.FC<FormAvancarEtapaProps> = ({
  empenho,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Estados COMPRAS → PAGAMENTO ──────────────────────────────────────────
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState<any>(null);
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [dadosBancariosSelecionados, setDadosBancariosSelecionados] = useState<FornecedorDadoBancario | null>(null);
  const [custosIniciais, setCustosIniciais] = useState<{
    id_item_empenho: number;
    id_produto: number;
    quantidade: number;       // quantidade original do empenho
    quantidadeSC: number;     // quantidade editável para a SC
    estoqueAtual: number;     // estoque atual do produto
    produto_nome: string;
    valor: number | null;
    valorRaw: string;
  }[]>([]);
  const [condicaoPagamento, setCondicaoPagamento] = useState<CondicaoPagamento>('A_VISTA');
  const [dataPrevisaoPagamento, setDataPrevisaoPagamento] = useState<Date | null>(null);
  const [numeroParcelas, setNumeroParcelas] = useState<number>(1);
  const [parcelas, setParcelas] = useState<{ numero_parcela: number; total_parcelas?: number; valor: number; data_vencimento: string }[]>([]);
  const [showQuickFornecedorForm, setShowQuickFornecedorForm] = useState(false);

  // ── Estados PAGAMENTO → NF_ENTRADA ───────────────────────────────────────
  const [solicitacaoCompleta, setSolicitacaoCompleta] = useState<SolicitacaoCompra | null>(null);
  const [parcelasEmpenho, setParcelasEmpenho] = useState<any[]>([]);
  const [isLoadingSolicitacao, setIsLoadingSolicitacao] = useState<boolean>(false);
  const [dataPagamento, setDataPagamento] = useState<Date | null>(null);
  const [arquivoComprovante, setArquivoComprovante] = useState<File | null>(null);
  const [confirmadoPagamento, setConfirmadoPagamento] = useState<boolean>(false);
  const [observacoesPagamento, setObservacoesPagamento] = useState<string>('');

  // ── Estados NF_ENTRADA → NF_SAIDA ──────────────────────────────────────
  const [numeroNfEntrada, setNumeroNfEntrada] = useState('');
  const [dataNfEntrada, setDataNfEntrada] = useState<Date | null>(new Date());
  const [arquivoNfEntrada, setArquivoNfEntrada] = useState<File | null>(null);

  // ── Estados NF_SAIDA → ENTREGA ───────────────────────────────────────────
  const [numeroNfVenda, setNumeroNfVenda] = useState('');
  const [dataEmissaoNf, setDataEmissaoNf] = useState<Date | null>(new Date());
  const [arquivoNfVenda, setArquivoNfVenda] = useState<File | null>(null);
  const [arquivoRomaneio, setArquivoRomaneio] = useState<File | null>(null);

  // ── Estados ENTREGA → CONCLUIDO ──────────────────────────────────────────
  const [dataVencimentoCr, setDataVencimentoCr] = useState<Date | null>(null);
  const [observacoesConclusao, setObservacoesConclusao] = useState<string>('');

  // ── Pré-calcular data de vencimento CR a partir do prazo do contrato ──────
  useEffect(() => {
    if (isOpen && empenho.etapa === 'ENTREGA' && empenho.contrato_prazo_vencimento) {
      const hoje = new Date();
      hoje.setDate(hoje.getDate() + Number(empenho.contrato_prazo_vencimento));
      setDataVencimentoCr(hoje);
    }
  }, [isOpen, empenho.etapa, empenho.contrato_prazo_vencimento]);

  // ── Helpers de data ───────────────────────────────────────────────────────
  const formatLocalYMD = (date: Date | null) => {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const parseLocalYMD = (ymd: string) => {
    if (!ymd) return null;
    const parts = ymd.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    return null;
  };

  // ── Carregar fornecedores ao abrir ────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      carregarFornecedores();
    }
  }, [isOpen]);

  // ── Inicializar custos (COMPRAS) ──────────────────────────────────────────
  useEffect(() => {
    if (isOpen && empenho.etapa === 'COMPRAS' && empenho.itens) {
      const custosInit = empenho.itens.map((item: any) => ({
        id_item_empenho: item.id,
        id_produto: item.id_produto,
        quantidade: Number(item.quantidade),
        quantidadeSC: Number(item.quantidade),
        estoqueAtual: Number(item.estoque_atual || 0),
        produto_nome: item.produto_nome,
        valor: null,
        valorRaw: ''
      }));
      setCustosIniciais(custosInit);
    }
  }, [isOpen, empenho]);

  // ── Buscar detalhes da solicitação (PAGAMENTO) ────────────────────────────
  useEffect(() => {
    if (isOpen && empenho.etapa === 'PAGAMENTO' && (empenho.id_solicitacao_compra || empenho.solicitacao_compra?.id)) {
      const idSolicitacao = empenho.id_solicitacao_compra || empenho.solicitacao_compra?.id;
      const fetchDetalhes = async () => {
        try {
          setIsLoadingSolicitacao(true);
          const response = await apiService.getSolicitacaoCompra(Number(idSolicitacao));
          if (response.success && response.data) {
            setSolicitacaoCompleta(response.data);
          }
          const responseParcelas = await apiService.getParcelasEmpenho(empenho.id);
          if (responseParcelas.success && responseParcelas.data) {
            setParcelasEmpenho(responseParcelas.data);
          }
        } catch (error) {
          console.error('Erro ao buscar detalhes para resumo:', error);
        } finally {
          setIsLoadingSolicitacao(false);
        }
      };
      fetchDetalhes();
    }
  }, [isOpen, empenho]);

  // ── Gerar parcelas automaticamente ───────────────────────────────────────
  useEffect(() => {
    if (numeroParcelas >= 1 && condicaoPagamento === 'A_PRAZO') {
      const valorTotalSolicitacao = custosIniciais.reduce((sum, c) => sum + (c.valor || 0) * c.quantidadeSC, 0);
      setParcelas(prev => {
        const totalAtual = prev.reduce((sum, p) => sum + p.valor, 0);
        if (prev.length !== numeroParcelas || Math.abs(totalAtual - valorTotalSolicitacao) > 0.02) {
          const novas = gerarParcelas(numeroParcelas, valorTotalSolicitacao);
          return novas.map((p, i) => ({
            ...p,
            data_vencimento: prev[i] ? prev[i].data_vencimento : ''
          }));
        }
        return prev;
      });
    }
  }, [numeroParcelas, condicaoPagamento, custosIniciais]);

  const gerarParcelas = (numParcelas: number, valorTotal: number) => {
    const valorBase = Math.floor((valorTotal / numParcelas) * 100) / 100;
    const diferenca = valorTotal - valorBase * numParcelas;
    return Array.from({ length: numParcelas }, (_, i) => ({
      numero_parcela: i + 1,
      total_parcelas: numParcelas,
      valor: parseFloat((i === numParcelas - 1 ? valorBase + diferenca : valorBase).toFixed(2)),
      data_vencimento: '',
    }));
  };

  const handleParcelaValorChange = (index: number, novoValorStr: string) => {
    const parsedValor = Math.max(0, parseFloat(novoValorStr) || 0);
    const novoValor = parseFloat(parsedValor.toFixed(2));
    setParcelas(prev => {
      if (prev.length <= 1) {
        const novas = [...prev];
        novas[index].valor = novoValor;
        return novas;
      }
      const novas = [...prev].map(p => ({ ...p }));
      const diferenca = novoValor - novas[index].valor;
      novas[index].valor = novoValor;
      const demaisIndices = novas.map((_, i) => i).filter(i => i !== index);
      const diferencaDistribuida = diferenca / demaisIndices.length;
      demaisIndices.forEach(idx => {
        novas[idx].valor = Math.max(0, novas[idx].valor - diferencaDistribuida);
      });
      const valorTotalSolicitacao = custosIniciais.reduce((sum, c) => sum + (c.valor || 0) * c.quantidadeSC, 0);
      const sumAll = novas.reduce((sum, p) => sum + p.valor, 0);
      const centDiff = valorTotalSolicitacao - sumAll;
      const lastIndexToAdjust = demaisIndices[demaisIndices.length - 1];
      novas[lastIndexToAdjust].valor = Math.max(0, novas[lastIndexToAdjust].valor + centDiff);
      novas.forEach(p => { p.valor = parseFloat(Math.round(p.valor * 100).toFixed(0)) / 100; });
      return novas;
    });
  };

  const carregarFornecedores = async () => {
    try {
      const response = await apiService.getFornecedores({});
      if (response.success && response.data) {
        const options = response.data.map((f: any) => ({
          value: f.id,
          label: f.tipo_entidade === 'PJ' ? f.razao_social : f.nome
        }));
        setFornecedores(options);
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const handleQuickFornecedorCreated = async (newFornecedor: any) => {
    try {
      const newOption = {
        value: newFornecedor.id,
        label: newFornecedor.tipo_entidade === 'PJ' ? newFornecedor.razao_social : newFornecedor.nome
      };
      setFornecedores(prev => [...prev, newOption]);
      setShowQuickFornecedorForm(false);
      setFornecedorSelecionado(newOption);
      toast.success('✓ Fornecedor cadastrado e selecionado!');
      carregarFornecedores();
    } catch (error) {
      console.error('Erro ao processar novo fornecedor:', error);
      toast.error('Erro ao processar o fornecedor');
      setShowQuickFornecedorForm(false);
    }
  };

  const getProximaEtapa = (etapa: EtapaEmpenho): string => {
    const fluxo: Record<string, string> = {
      COMPRAS: 'PAGAMENTO',
      PAGAMENTO: 'NF_ENTRADA',
      NF_ENTRADA: 'NF_SAIDA',
      NF_SAIDA: 'ENTREGA',
      ENTREGA: 'CONCLUIDO'
    };
    return fluxo[etapa] || '';
  };

  // ── Pular para NF_SAIDA via estoque ──────────────────────────────────────
  const handleSkipToNFSaida = async () => {
    const confirmacao = window.confirm(
      'Tem certeza que deseja usar o estoque existente?\n\n' +
      'O empenho avançará direto para NF de Saída sem gerar:\n' +
      '- Solicitação de Compra\n' +
      '- Contas a Pagar\n' +
      '- NF de Entrada\n\n' +
      'Esta ação pode ser revertida retrocedendo a etapa.'
    );
    if (!confirmacao) return;

    setIsSubmitting(true);
    try {
      const response = await apiService.avancarEtapaEmpenho({
        id_empenho: empenho.id,
        dados: { pular_para_nf_saida: true }
      } as any);
      if (response.success) {
        toast.success('Empenho avançado para NF de Saída (estoque suficiente)');
        resetForm();
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao avançar etapa');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let dadosTransicao: any = {};

      if (empenho.etapa === 'COMPRAS') {
        if (!fornecedorSelecionado) {
          toast.error('Selecione o fornecedor');
          setIsSubmitting(false);
          return;
        }
        if (!dadosBancariosSelecionados) {
          toast.error('Selecione os dados bancários para pagamento');
          setIsSubmitting(false);
          return;
        }
        const custosInvalidos = custosIniciais.filter(c => c.valor === null || c.valor <= 0);
        if (custosInvalidos.length > 0) {
          toast.error('Todos os custos unitários devem ser preenchidos');
          setIsSubmitting(false);
          return;
        }
        dadosTransicao.id_fornecedor = fornecedorSelecionado.value;
        dadosTransicao.itens_custo = custosIniciais.map(c => ({
          id_produto: c.id_produto,
          valor_custo_unitario: c.valor,
          quantidade_sc: c.quantidadeSC
        }));
        dadosTransicao.condicao_pagamento = condicaoPagamento;
        dadosTransicao.id_dados_bancarios = dadosBancariosSelecionados.id;
        if (condicaoPagamento === 'A_VISTA') {
          if (!dataPrevisaoPagamento) {
            toast.error('Informe a data prevista de pagamento');
            setIsSubmitting(false);
            return;
          }
          dadosTransicao.data_vencimento = formatLocalYMD(dataPrevisaoPagamento);
        } else if (condicaoPagamento === 'A_PRAZO') {
          if (!parcelas.every(p => p.data_vencimento)) {
            toast.error('Preencha todas as datas de vencimento das parcelas');
            setIsSubmitting(false);
            return;
          }
          dadosTransicao.numero_parcelas = numeroParcelas;
          dadosTransicao.parcelas = parcelas.map(p => ({
            numero_parcela: p.numero_parcela,
            total_parcelas: p.total_parcelas || numeroParcelas,
            valor: p.valor,
            data_vencimento: p.data_vencimento
          }));
        }

      } else if (empenho.etapa === 'PAGAMENTO') {
        if (!confirmadoPagamento) {
          toast.error('Confirme que o pagamento foi realizado');
          setIsSubmitting(false);
          return;
        }
        if (!dataPagamento) {
          toast.error('Informe a data efetiva do pagamento');
          setIsSubmitting(false);
          return;
        }
        if (!arquivoComprovante) {
          toast.error('O comprovante de pagamento é obrigatório');
          setIsSubmitting(false);
          return;
        }
        dadosTransicao.confirmado = true;
        dadosTransicao.data_pagamento = formatLocalYMD(dataPagamento);
        if (observacoesPagamento.trim()) {
          dadosTransicao.observacoes = observacoesPagamento.trim();
        }

      } else if (empenho.etapa === 'NF_ENTRADA') {
        if (!numeroNfEntrada.trim()) {
          toast.error('Informe o número da Nota Fiscal de Entrada');
          setIsSubmitting(false);
          return;
        }
        if (!dataNfEntrada) {
          toast.error('Informe a data da NF de Entrada');
          setIsSubmitting(false);
          return;
        }
        dadosTransicao.numero_nf_entrada = numeroNfEntrada.trim();
        dadosTransicao.data_nf_entrada = formatLocalYMD(dataNfEntrada);

      } else if (empenho.etapa === 'NF_SAIDA') {
        if (!numeroNfVenda.trim()) {
          toast.error('Informe o número da Nota Fiscal de Venda');
          setIsSubmitting(false);
          return;
        }
        if (!dataEmissaoNf) {
          toast.error('Informe a data de emissão da NF');
          setIsSubmitting(false);
          return;
        }
        dadosTransicao.numero_nf_venda = numeroNfVenda.trim();
        dadosTransicao.data_emissao_nf_venda = formatLocalYMD(dataEmissaoNf);

      } else if (empenho.etapa === 'ENTREGA') {
        if (!dataVencimentoCr) {
          toast.error('Informe a data de vencimento do Contas a Receber');
          setIsSubmitting(false);
          return;
        }
        if (!arquivoRomaneio) {
          toast.error('O arquivo de romaneio é obrigatório');
          setIsSubmitting(false);
          return;
        }
        dadosTransicao.data_vencimento_cr = formatLocalYMD(dataVencimentoCr);
        if (observacoesConclusao.trim()) {
          dadosTransicao.observacoes = observacoesConclusao.trim();
        }
      }

      const payload = {
        id_empenho: empenho.id,
        dados: dadosTransicao
      };

      const response = await apiService.avancarEtapaEmpenho(payload as any);

      if (response.success) {
        let uploadSucesso = true;

        // Upload comprovante (PAGAMENTO → NF_ENTRADA)
        if (empenho.etapa === 'PAGAMENTO' && arquivoComprovante) {
          try {
            const uploadResponse = await apiService.createAnexoEmpenho({
              id_empenho: empenho.id,
              id_contrato: empenho.id_contrato,
              tipo_documento: 'Comprovante de Pagamento',
              titulo: `Comprovante - ${dataPagamento?.toLocaleDateString('pt-BR')}`,
              descricao: `Comprovante de pagamento de ${dataPagamento?.toLocaleDateString('pt-BR')}`,
              arquivos: [arquivoComprovante]
            });
            if (!uploadResponse.success) {
              uploadSucesso = false;
              toast.warning('Etapa avançada, mas o comprovante não foi enviado. Adicione-o manualmente pela aba Anexos.');
            }
          } catch {
            uploadSucesso = false;
            toast.warning('Etapa avançada, mas o comprovante não foi enviado. Adicione-o manualmente pela aba Anexos.');
          }

          // Anexar comprovante na primeira Conta a Pagar
          const primeiraParcelaComCP = parcelasEmpenho.find((p: any) => p.id_conta_pagar);
          if (primeiraParcelaComCP) {
            try {
              await apiService.createAnexoFinanceiro({
                arquivo: arquivoComprovante,
                tipo_origem: 'CONTA_PAGAR',
                id_origem: primeiraParcelaComCP.id_conta_pagar,
                titulo: `Comprovante - ${dataPagamento?.toLocaleDateString('pt-BR')}`,
                descricao: `Comprovante de pagamento do empenho ${empenho.codigo_empenho}`,
              });
            } catch {
              toast.warning('Comprovante salvo no empenho, mas não foi vinculado à Conta a Pagar.');
            }
          }
        }

        // Upload NF de entrada (NF_ENTRADA → NF_SAIDA)
        if (empenho.etapa === 'NF_ENTRADA' && arquivoNfEntrada) {
          try {
            const uploadResponse = await apiService.createAnexoEmpenho({
              id_empenho: empenho.id,
              id_contrato: empenho.id_contrato,
              tipo_documento: 'Nota Fiscal de Entrada',
              titulo: `NF Entrada ${numeroNfEntrada}`,
              descricao: `Nota Fiscal de Entrada nº ${numeroNfEntrada} de ${dataNfEntrada?.toLocaleDateString('pt-BR')}`,
              arquivos: [arquivoNfEntrada]
            });
            if (!uploadResponse.success) {
              uploadSucesso = false;
              toast.warning('Etapa avançada, mas a NF de Entrada não foi enviada. Adicione-a manualmente pela aba Anexos.');
            }
          } catch {
            uploadSucesso = false;
            toast.warning('Etapa avançada, mas a NF de Entrada não foi enviada. Adicione-a manualmente pela aba Anexos.');
          }
        }

        // Upload NF de venda (NF_SAIDA → ENTREGA)
        if (empenho.etapa === 'NF_SAIDA' && arquivoNfVenda) {
          try {
            const uploadResponse = await apiService.createAnexoEmpenho({
              id_empenho: empenho.id,
              id_contrato: empenho.id_contrato,
              tipo_documento: 'Nota Fiscal de Saída',
              titulo: `NF Venda ${numeroNfVenda}`,
              descricao: `Nota Fiscal de Venda nº ${numeroNfVenda} emitida em ${dataEmissaoNf?.toLocaleDateString('pt-BR')}`,
              arquivos: [arquivoNfVenda]
            });
            if (!uploadResponse.success) {
              uploadSucesso = false;
              toast.warning('Etapa avançada, mas a NF de Venda não foi enviada. Adicione-a manualmente pela aba Anexos.');
            }
          } catch {
            uploadSucesso = false;
            toast.warning('Etapa avançada, mas a NF de Venda não foi enviada. Adicione-a manualmente pela aba Anexos.');
          }
        }

        // Upload Romaneio (ENTREGA → CONCLUIDO)
        if (empenho.etapa === 'ENTREGA' && arquivoRomaneio) {
          try {
            const uploadResponse = await apiService.createAnexoEmpenho({
              id_empenho: empenho.id,
              id_contrato: empenho.id_contrato,
              tipo_documento: 'Romaneio',
              titulo: `Romaneio - Empenho ${empenho.codigo_empenho}`,
              descricao: `Romaneio da entrega do empenho ${empenho.codigo_empenho}`,
              arquivos: [arquivoRomaneio]
            });
            if (!uploadResponse.success) {
              uploadSucesso = false;
              toast.warning('Etapa avançada, mas o romaneio não foi enviado. Adicione-o manualmente pela aba Anexos.');
            }
          } catch {
            uploadSucesso = false;
            toast.warning('Etapa avançada, mas o romaneio não foi enviado. Adicione-o manualmente pela aba Anexos.');
          }
        }

        if (uploadSucesso) {
          toast.success('Etapa avançada com sucesso!');
        }

        resetForm();
        onSuccess();
      } else {
        throw new Error(response.message || 'Erro ao avançar etapa');
      }
    } catch (error: any) {
      console.error('Erro ao avançar etapa:', error);
      toast.error(error.message || 'Erro ao avançar etapa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFornecedorSelecionado(null);
    setCustosIniciais([]);
    setCondicaoPagamento('A_VISTA');
    setDataPrevisaoPagamento(null);
    setNumeroParcelas(1);
    setParcelas([]);
    setShowQuickFornecedorForm(false);
    setSolicitacaoCompleta(null);
    setParcelasEmpenho([]);
    setDataPagamento(null);
    setArquivoComprovante(null);
    setConfirmadoPagamento(false);
    setObservacoesPagamento('');
    setNumeroNfEntrada('');
    setDataNfEntrada(new Date());
    setArquivoNfEntrada(null);
    setNumeroNfVenda('');
    setDataEmissaoNf(new Date());
    setArquivoNfVenda(null);
    setArquivoRomaneio(null);
    setDataVencimentoCr(null);
    setObservacoesConclusao('');
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  const proximaEtapa = getProximaEtapa(empenho.etapa);

  const formatarValor = (valor: number): string => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const valorTotalEmpenho = empenho.itens?.reduce((sum, item) => sum + (item.valor_total ?? item.quantidade * item.valor_unitario), 0) ?? empenho.valor_total;

  // ── Renderizar formulário ─────────────────────────────────────────────────
  const renderFormulario = () => {
    switch (empenho.etapa) {

      // ── COMPRAS → PAGAMENTO ──────────────────────────────────────────────
      case 'COMPRAS':
        return (
          <div>
            <div className="mb-4">
              <i className="ti ti-shopping-cart fs-1 text-primary mb-3 d-block text-center" />
              <p className="lead mb-3 text-center">
                Preencha os dados para criar a Solicitação de Compra.
              </p>
            </div>

            <div className="mb-4">
              <label className="form-label">Fornecedor <span className="text-danger">*</span></label>
              <Select
                options={fornecedores}
                value={fornecedorSelecionado}
                onChange={(selected) => setFornecedorSelecionado(selected)}
                placeholder="Selecione o fornecedor..."
                isClearable
                classNamePrefix="react-select"
                isDisabled={isSubmitting}
                noOptionsMessage={() => "Nenhum fornecedor encontrado"}
              />
              {!showQuickFornecedorForm && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => setShowQuickFornecedorForm(true)}
                  disabled={isSubmitting}
                >
                  <i className="ti ti-plus me-1" />
                  Cadastrar novo fornecedor
                </button>
              )}
              {showQuickFornecedorForm && (
                <div className="row mb-3 mt-3">
                  <div className="col-12">
                    <div className="card border-primary" style={{ boxShadow: 'none' }}>
                      <div className="card-body">
                        <QuickFornecedorForm
                          onSuccess={handleQuickFornecedorCreated}
                          onCancel={() => setShowQuickFornecedorForm(false)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="form-text mt-2">Selecione de qual fornecedor será feita a compra dos itens.</div>
            </div>

            {/* Alerta: estoque suficiente para todos os itens */}
            {custosIniciais.length > 0 && custosIniciais.every(item => item.estoqueAtual >= item.quantidade) && (
              <div className="alert alert-success d-flex align-items-start gap-3 mb-4">
                <i className="ti ti-package-check fs-2 mt-1" />
                <div className="flex-grow-1">
                  <h6 className="alert-heading mb-1">Estoque suficiente para todos os itens!</h6>
                  <p className="mb-2 small">
                    Todos os itens possuem estoque disponível. Você pode pular as etapas de Pagamento e NF de Entrada,
                    indo direto para NF de Saída.
                  </p>
                  <div className="table-responsive mb-2">
                    <table className="table table-sm table-borderless mb-0">
                      <thead>
                        <tr>
                          <th>Produto</th>
                          <th className="text-end">Necessário</th>
                          <th className="text-end">Em Estoque</th>
                        </tr>
                      </thead>
                      <tbody>
                        {custosIniciais.map((item, i) => (
                          <tr key={i}>
                            <td className="small">{item.produto_nome}</td>
                            <td className="text-end small">{item.quantidade}</td>
                            <td className="text-end small text-success fw-bold">{item.estoqueAtual}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleSkipToNFSaida}
                    disabled={isSubmitting}
                  >
                    <i className="ti ti-arrow-forward me-1" />
                    Pular para NF de Saída (usar estoque existente)
                  </button>
                </div>
              </div>
            )}

            <h6 className="mb-3 mt-4">Preencha os Custos Unitários</h6>
            <div className="table-responsive mb-4">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-end">Qtd Empenho</th>
                    <th className="text-end">Estoque</th>
                    <th className="text-end">Qtd Compra *</th>
                    <th className="text-end">Custo Unit. *</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {custosIniciais.map((item, index) => {
                    const total = (item.valor || 0) * item.quantidadeSC;
                    const diferenca = item.quantidadeSC - item.quantidade;
                    return (
                      <tr key={index}>
                        <td>{item.produto_nome}</td>
                        <td className="text-end">{item.quantidade}</td>
                        <td className="text-end">
                          <span className={item.estoqueAtual >= item.quantidade ? 'text-success fw-bold' : 'text-warning fw-bold'}>
                            {item.estoqueAtual}
                          </span>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-end"
                            min="0"
                            step="0.001"
                            value={item.quantidadeSC}
                            onChange={(e) => {
                              const novaQtd = parseFloat(e.target.value) || 0;
                              setCustosIniciais(prev => prev.map((c, i) =>
                                i === index ? { ...c, quantidadeSC: novaQtd } : c
                              ));
                            }}
                            onWheel={(e) => (e.target as HTMLElement).blur()}
                            disabled={isSubmitting}
                          />
                          {diferenca !== 0 && (
                            <small className={`d-block text-end mt-1 ${diferenca < 0 ? 'text-warning' : 'text-info'}`}>
                              {diferenca > 0 ? '+' : ''}{diferenca.toFixed(3).replace(/\.?0+$/, '')}
                            </small>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control form-control-sm text-end"
                            placeholder="0,00"
                            value={item.valorRaw}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const normalizado = raw.replace(/\./g, '').replace(',', '.');
                              const novoCusto = raw === '' || raw === ',' || raw.endsWith(',') ? null : (parseFloat(normalizado) || null);
                              setCustosIniciais(prev => prev.map((c, i) => i === index ? { ...c, valor: novoCusto, valorRaw: raw } : c));
                            }}
                            disabled={isSubmitting}
                            required
                          />
                        </td>
                        <td className="text-end">{item.valor ? formatarValor(total) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="table-active">
                    <td colSpan={5} className="text-end fw-bold">Total:</td>
                    <td className="text-end fw-bold">
                      {formatarValor(custosIniciais.reduce((sum, c) => sum + ((c.valor || 0) * c.quantidadeSC), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {fornecedorSelecionado && (
              <div className="mb-4">
                <SelecionarDadosBancarios
                  idFornecedor={fornecedorSelecionado.value}
                  idConta={empenho.id_conta || 0}
                  selectedId={dadosBancariosSelecionados?.id}
                  onSelect={(id, dados) => setDadosBancariosSelecionados(dados)}
                  disabled={isSubmitting}
                />
              </div>
            )}

            <h6 className="mb-3 mt-4">Condição de Pagamento</h6>
            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label">Condição de Pagamento *</label>
                <div className="btn-group w-100" role="group">
                  <input type="radio" className="btn-check" name="condicao_inicial" id="a_vista_inicial"
                    checked={condicaoPagamento === 'A_VISTA'} onChange={() => setCondicaoPagamento('A_VISTA')} disabled={isSubmitting} />
                  <label className="btn" htmlFor="a_vista_inicial" style={{
                    backgroundColor: condicaoPagamento === 'A_VISTA' ? '#00918E' : 'transparent',
                    color: condicaoPagamento === 'A_VISTA' ? '#fff' : '#00918E',
                    borderColor: '#00918E'
                  }}>À Vista</label>
                  <input type="radio" className="btn-check" name="condicao_inicial" id="a_prazo_inicial"
                    checked={condicaoPagamento === 'A_PRAZO'} onChange={() => setCondicaoPagamento('A_PRAZO')} disabled={isSubmitting} />
                  <label className="btn" htmlFor="a_prazo_inicial" style={{
                    backgroundColor: condicaoPagamento === 'A_PRAZO' ? '#00918E' : 'transparent',
                    color: condicaoPagamento === 'A_PRAZO' ? '#fff' : '#00918E',
                    borderColor: '#00918E'
                  }}>A Prazo</label>
                </div>
              </div>

              {condicaoPagamento === 'A_VISTA' && (
                <div className="col-md-12">
                  <label className="form-label">Data de Previsão de Pagamento</label>
                  <DatePicker
                    selected={dataPrevisaoPagamento}
                    onChange={(date) => setDataPrevisaoPagamento(date)}
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    locale="pt-BR"
                    minDate={new Date()}
                    placeholderText="Selecione"
                    disabled={isSubmitting}
                  />
                  <div className="form-text">Data prevista para o pagamento ao fornecedor</div>
                </div>
              )}

              {condicaoPagamento === 'A_PRAZO' && (
                <div className="col-md-6">
                  <label className="form-label">Número de Parcelas *</label>
                  <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()}
                    className="form-control" min="1" max="12" value={numeroParcelas}
                    onChange={(e) => setNumeroParcelas(parseInt(e.target.value) || 1)}
                    disabled={isSubmitting} required />
                </div>
              )}

              {condicaoPagamento === 'A_PRAZO' && parcelas.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">Parcelas</h6>
                  <p className="text-muted small mb-3">
                    Valor total: {formatarValor(custosIniciais.reduce((sum, c) => sum + (c.valor || 0) * c.quantidadeSC, 0))}
                  </p>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Parcela</th>
                          <th className="text-end">Valor</th>
                          <th>Data de Vencimento *</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcelas.map((parcela, index) => (
                          <tr key={index}>
                            <td><span className="badge bg-secondary">{parcela.numero_parcela}/{parcelas.length}</span></td>
                            <td className="text-end">
                              <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()}
                                className="form-control form-control-sm text-end" step="0.01" min="0"
                                value={parcela.valor || ''}
                                onChange={(e) => handleParcelaValorChange(index, e.target.value)}
                                disabled={isSubmitting} required />
                            </td>
                            <td>
                              <DatePicker
                                selected={parseLocalYMD(parcela.data_vencimento)}
                                onChange={(date) => {
                                  const novasParcelas = [...parcelas];
                                  novasParcelas[index].data_vencimento = formatLocalYMD(date);
                                  setParcelas(novasParcelas);
                                }}
                                className="form-control form-control-sm"
                                dateFormat="dd/MM/yyyy"
                                locale="pt-BR"
                                minDate={new Date()}
                                placeholderText="Selecione"
                                disabled={isSubmitting}
                                required
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-active">
                          <td colSpan={2} className="text-end fw-bold">Total:</td>
                          <td className="text-end fw-bold">{formatarValor(parcelas.reduce((sum, p) => sum + p.valor, 0))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {parcelas.some(p => !p.data_vencimento) && (
                    <div className="alert alert-warning mt-2">
                      <i className="bi bi-exclamation-triangle me-2" />
                      Preencha todas as datas de vencimento das parcelas.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="alert alert-info mb-0 mt-4">
              <strong>O que acontecerá:</strong>
              <ul className="list-unstyled mb-0 mt-2">
                <li>✓ Solicitação de Compra criada (fornecedor, custos e condição de pagamento)</li>
                <li>✓ <strong>Contas a Pagar geradas</strong> automaticamente para cada parcela</li>
                <li>✓ Dados bancários vinculados ao pagamento</li>
              </ul>
            </div>
          </div>
        );

      // ── PAGAMENTO → NF_ENTRADA ───────────────────────────────────────────
      case 'PAGAMENTO':
        return (
          <div>
            <div className="mb-4">
              <i className="ti ti-credit-card fs-1 text-primary mb-3 d-block text-center" />
              <p className="lead mb-3 text-center">
                Registre o pagamento realizado ao fornecedor.
              </p>
            </div>

            {/* Resumo dos Itens */}
            <div className="card mb-4">
              <div className="card-body">
                <h6 className="mb-3">Itens da Solicitação de Compra</h6>
                {isLoadingSolicitacao ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                    <span className="text-muted">Carregando itens...</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Produto</th>
                          <th className="text-end">Qtd Empenho</th>
                          <th className="text-end">Qtd Compra</th>
                          <th className="text-end">Estoque Atual</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(solicitacaoCompleta?.itens || empenho.solicitacao_compra?.itens || []).map((item: any, index: number) => {
                          const itemEmpenho = empenho.itens?.find((ie: any) => ie.id_produto === item.id_produto);
                          const estoqueAtual = itemEmpenho?.estoque_atual ?? '-';
                          const qtdEmpenho = item.quantidade_empenho != null ? Number(item.quantidade_empenho) : '-';
                          const qtdCompra = Number(item.quantidade);
                          return (
                            <tr key={index}>
                              <td>{item.produto_nome || `Produto #${item.id_produto}`}</td>
                              <td className="text-end">{qtdEmpenho}</td>
                              <td className="text-end">{qtdCompra}</td>
                              <td className="text-end">
                                {estoqueAtual !== '-' ? (
                                  <span className={Number(estoqueAtual) >= qtdCompra ? 'text-success fw-bold' : 'text-warning fw-bold'}>
                                    {Number(estoqueAtual)}
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Resumo das CPs */}
            <div className="card mb-4">
              <div className="card-body">
                <h6 className="mb-3">Contas a Pagar Geradas</h6>

                <div className="mb-3">
                  <strong>Fornecedor:</strong>
                  <p className="mb-0">
                    {solicitacaoCompleta?.fornecedor_nome ||
                     solicitacaoCompleta?.fornecedor_razao ||
                     empenho.solicitacao_compra?.fornecedor_razao ||
                     empenho.solicitacao_compra?.fornecedor_fantasia ||
                     'Não informado'}
                  </p>
                </div>

                {isLoadingSolicitacao ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                    <span className="text-muted">Carregando contas a pagar...</span>
                  </div>
                ) : parcelasEmpenho.length > 0 ? (
                  <div className="mb-3">
                    <table className="table table-sm mt-2">
                      <thead>
                        <tr>
                          <th>Parcela</th>
                          <th className="text-end">Valor</th>
                          <th>Vencimento</th>
                          <th>Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcelasEmpenho.map((parcela: any, index: number) => (
                          <tr key={index}>
                            <td><span className="badge bg-secondary">{parcela.numero_parcela}/{parcelasEmpenho.length}</span></td>
                            <td className="text-end">{formatarValor(parcela.valor)}</td>
                            <td>{parcela.data_vencimento ? parcela.data_vencimento.split('-').reverse().join('/') : '-'}</td>
                            <td><span className="badge badge-soft-warning">{parcela.situacao || 'EM_ABERTO'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="fw-bold">
                          <td colSpan={2} className="text-end">Total:</td>
                          <td colSpan={2}>{formatarValor(parcelasEmpenho.reduce((sum: number, p: any) => sum + Number(p.valor), 0))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="mb-3">
                    <strong>Valor Total:</strong>
                    <p className="mb-0">{formatarValor(empenho.solicitacao_compra?.valor_total_custo || 0)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Data do pagamento */}
            <div className="mb-3">
              <label className="form-label">Data Efetiva do Pagamento <span className="text-danger">*</span></label>
              <DatePicker
                selected={dataPagamento}
                onChange={(date) => setDataPagamento(date)}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                locale="pt-BR"
                maxDate={new Date()}
                placeholderText="Selecione a data em que o pagamento foi feito"
                disabled={isSubmitting}
              />
            </div>

            {/* Upload do comprovante */}
            <div className="mb-4">
              <AnexoUpload
                label="Comprovante de Pagamento"
                obrigatorio={true}
                onFileSelect={(file) => setArquivoComprovante(file)}
                disabled={isSubmitting}
              />
            </div>

            {/* Observações */}
            <div className="mb-4">
              <label className="form-label">Observações (opcional)</label>
              <textarea
                className="form-control"
                rows={2}
                value={observacoesPagamento}
                onChange={(e) => setObservacoesPagamento(e.target.value)}
                disabled={isSubmitting}
                placeholder="Informações adicionais sobre o pagamento..."
              />
            </div>

            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="checkboxConfirmacaoPagamento"
                checked={confirmadoPagamento}
                onChange={(e) => setConfirmadoPagamento(e.target.checked)}
                disabled={isSubmitting}
              />
              <label className="form-check-label fw-bold text-primary" htmlFor="checkboxConfirmacaoPagamento">
                Confirmo que o pagamento foi realizado ao fornecedor.
              </label>
            </div>

            <div className="alert alert-warning mb-0">
              <i className="ti ti-alert-triangle me-2" />
              <strong>Ao avançar, todas as Contas a Pagar serão marcadas como PAGAS automaticamente.</strong>
            </div>
          </div>
        );

      // ── NF_ENTRADA → NF_SAIDA ────────────────────────────────────────────
      case 'NF_ENTRADA':
        return (
          <div>
            <div className="mb-4">
              <i className="ti ti-file-invoice fs-1 text-primary mb-3 d-block text-center" />
              <p className="lead mb-3 text-center">
                Informe a Nota Fiscal recebida do fornecedor.
              </p>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label">Número da NF de Entrada <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ex: 12345"
                  value={numeroNfEntrada}
                  onChange={(e) => setNumeroNfEntrada(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Data da NF de Entrada <span className="text-danger">*</span></label>
                <DatePicker
                  selected={dataNfEntrada}
                  onChange={(date) => setDataNfEntrada(date)}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  maxDate={new Date()}
                  placeholderText="Selecione"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <AnexoUpload
                label="Anexar NF de Entrada (PDF)"
                obrigatorio={false}
                onFileSelect={(file) => setArquivoNfEntrada(file)}
                disabled={isSubmitting}
              />
            </div>

            <div className="alert alert-info mb-0">
              <i className="ti ti-info-circle me-2" />
              <strong>O estoque será atualizado automaticamente</strong> com a entrada dos itens ao avançar esta etapa.
            </div>
          </div>
        );

      // ── NF_SAIDA → ENTREGA ───────────────────────────────────────────────
      case 'NF_SAIDA':
        return (
          <div>
            <div className="mb-4">
              <i className="ti ti-file-invoice-dollar fs-1 text-primary mb-3 d-block text-center" />
              <p className="lead mb-3 text-center">
                Informe a Nota Fiscal de Venda emitida para o cliente.
              </p>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label">Número da NF de Venda <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ex: 54321"
                  value={numeroNfVenda}
                  onChange={(e) => setNumeroNfVenda(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Data de Emissão <span className="text-danger">*</span></label>
                <DatePicker
                  selected={dataEmissaoNf}
                  onChange={(date) => setDataEmissaoNf(date)}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  maxDate={new Date()}
                  placeholderText="Selecione"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <AnexoUpload
                label="Anexar NF de Venda (PDF)"
                obrigatorio={false}
                onFileSelect={(file) => setArquivoNfVenda(file)}
                disabled={isSubmitting}
              />
            </div>

            <div className="alert alert-info mb-0">
              <i className="ti ti-info-circle me-2" />
              Ao avançar, o empenho será marcado como <strong>Entrega</strong>. O Contas a Receber será gerado na próxima etapa.
            </div>
          </div>
        );

      // ── ENTREGA → CONCLUIDO ──────────────────────────────────────────────
      case 'ENTREGA':
        return (
          <div>
            <div className="mb-4">
              <i className="ti ti-circle-check fs-1 text-success mb-3 d-block text-center" />
              <p className="lead mb-3 text-center">
                Defina o vencimento do Contas a Receber para concluir o empenho.
              </p>
            </div>

            {/* Resumo do CR que será gerado */}
            <div className="card border-success mb-4">
              <div className="card-header bg-success text-white py-2">
                <i className="ti ti-receipt me-2" />
                <strong>Conta a Receber que será gerada</strong>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm mb-2">
                    <thead className="table-light">
                      <tr>
                        <th>Produto</th>
                        <th className="text-end">Unidade</th>
                        <th className="text-end">Qtd</th>
                        <th className="text-end">Vlr Unit.</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empenho.itens?.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.produto_nome || `Produto #${item.id_produto}`}</td>
                          <td className="text-end">{item.unidade_medida || '-'}</td>
                          <td className="text-end">{Number(item.quantidade)}</td>
                          <td className="text-end">{formatarValor(item.valor_unitario)}</td>
                          <td className="text-end fw-bold">{formatarValor(item.valor_total ?? item.quantidade * item.valor_unitario)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-active fw-bold">
                        <td colSpan={4} className="text-end">Valor Total do CR:</td>
                        <td className="text-end text-success fs-6">{formatarValor(valorTotalEmpenho)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Data de Vencimento do Contas a Receber <span className="text-danger">*</span>
                {empenho.contrato_prazo_vencimento && (
                  <span className="text-muted ms-2 fw-normal small">(pré-calculado: hoje + {empenho.contrato_prazo_vencimento} dias)</span>
                )}
              </label>
              <DatePicker
                selected={dataVencimentoCr}
                onChange={(date) => setDataVencimentoCr(date)}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                locale="pt-BR"
                minDate={new Date()}
                placeholderText="Selecione a data de vencimento"
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-3">
              <AnexoUpload
                label="Anexar Romaneio (PDF)"
                obrigatorio={true}
                onFileSelect={(file) => setArquivoRomaneio(file)}
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Observações (opcional)</label>
              <textarea
                className="form-control"
                rows={2}
                value={observacoesConclusao}
                onChange={(e) => setObservacoesConclusao(e.target.value)}
                disabled={isSubmitting}
                placeholder="Informações adicionais sobre a conclusão..."
              />
            </div>

            {dataVencimentoCr && (
              <div className="alert alert-success mb-0">
                <i className="ti ti-check me-2" />
                Será gerada uma <strong>Conta a Receber</strong> no valor de{' '}
                <strong>{formatarValor(valorTotalEmpenho)}</strong> com vencimento em{' '}
                <strong>{dataVencimentoCr.toLocaleDateString('pt-BR')}</strong>.
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header text-white" style={{ backgroundColor: '#00918E' }}>
              <h5 className="modal-title">
                <i className="ti ti-arrow-right me-2" />
                Avançar Etapa
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={handleClose} disabled={isSubmitting} />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Indicador de progresso */}
                <div className="mb-4 text-center">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div>
                      <div className="badge badge-soft-info p-3">{empenho.etapa}</div>
                      <div className="text-muted small mt-1">Atual</div>
                    </div>
                    <i className="ti ti-arrow-right fs-3 text-primary" />
                    <div>
                      <div className="badge badge-soft-success p-3">{proximaEtapa}</div>
                      <div className="text-muted small mt-1">Próxima</div>
                    </div>
                  </div>
                </div>

                {renderFormulario()}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processando...
                    </>
                  ) : empenho.etapa === 'ENTREGA' ? (
                    <>
                      <i className="ti ti-circle-check me-2" />
                      Concluir e Gerar CR
                    </>
                  ) : empenho.etapa === 'PAGAMENTO' ? (
                    <>
                      <i className="ti ti-credit-card me-2" />
                      Confirmar Pagamento
                    </>
                  ) : (
                    <>
                      <i className="ti ti-check me-2" />
                      Confirmar Avanço
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={handleClose} />
    </>
  );
};

export default FormAvancarEtapa;
